import TouchApp from "@mui/icons-material/TouchApp";
import {
  Abc,
  Backspace,
  CalendarToday,
  Casino,
  Check,
  CropTwoTone,
  DataObject,
  FindInPage,
  Fingerprint,
  HourglassBottom,
  KeyboardReturn,
  Launch,
  Link,
  RestartAlt,
  SwipeDown,
  SwipeLeft,
  SwipeUp,
  RemoveRedEye,
  WifiOff,
  Wifi,
  SettingsEthernet,
  ZoomIn,
  ZoomOut,
  Camera,
  PermDeviceInformation,
  ArrowBack,
  AudioFileOutlined,
  SearchOff,
  Mic,
  Email,
  Home,
} from "@mui/icons-material";
import React from "react";
import {
  type AudioFile,
  type SettingsData,
  type TemplateWithLabel,
} from "../utils/types";

const TIPTAP_COMMANDS_SUGGESTIONS = [
  {
    title: "Tap on with AI",
    autocompleteTemplate: "Tap on <element>",
    alternatives: ["tap", "click"],
    icon: <TouchApp />,
  },
  {
    title: "Tap on id",
    autocompleteTemplate: 'tapOn.id: "<element id>"',
    alternatives: ["tap", "click"],
    icon: <TouchApp />,
  },
  {
    title: "Tap on text",
    autocompleteTemplate: 'tapOn: "<element>"',
    alternatives: ["tap", "click"],
    icon: <TouchApp />,
  },
  {
    title: "Tap on center of the screen",
    autocompleteTemplate: "tapOn: center",
    alternatives: ["tap", "click", "center"],
    icon: <TouchApp />,
  },
  {
    title: "Long tap with AI",
    autocompleteTemplate: "Long tap on <element>",
    alternatives: ["tap", "click", "long"],
    icon: <TouchApp />,
  },
  {
    title: "Long tap on id",
    autocompleteTemplate: 'longPressOn.id: "<element id>" duration: <seconds>',
    alternatives: ["tap", "click"],
    icon: <TouchApp />,
  },
  {
    title: "Long tap on text",
    autocompleteTemplate: 'longPressOn: "<element>" duration: <seconds>',
    alternatives: ["tap", "click"],
    icon: <TouchApp />,
  },
  {
    title: "Multi tap on id",
    autocompleteTemplate: 'multiTap.id: "<element id>" count: <number>',
    alternatives: ["tap", "multi", "double", "triple"],
    icon: <TouchApp />,
  },
  {
    title: "Multi tap on text",
    autocompleteTemplate: 'multiTap.text: "<element>" count: <number>',
    alternatives: ["tap", "multi", "double", "triple"],
    icon: <TouchApp />,
  },
  {
    title: "Zoom in",
    autocompleteTemplate: "zoom: in",
    alternatives: ["zoom", "in"],
    icon: <ZoomIn />,
  },
  {
    title: "Zoom out",
    autocompleteTemplate: "zoom: out",
    alternatives: ["zoom", "out"],
    icon: <ZoomOut />,
  },
  {
    title: "Type text",
    autocompleteTemplate: 'type: "<text>"',
    alternatives: ["type", "write"],
    icon: <Abc />,
  },
  {
    title: "Play voice",
    autocompleteTemplate: 'playVoice: "<text>"',
    alternatives: ["voice"],
    icon: <Mic />,
  },
  {
    title: "Scroll",
    autocompleteTemplate: "scroll: <direction>",
    alternatives: ["scroll", "swipe", "slide"],
    icon: <SwipeUp />,
  },
  {
    title: "Scroll up until id visible",
    autocompleteTemplate: 'scrollUpUntilVisible.id: "<element id>"',
    alternatives: ["scroll", "swipe", "slide"],
    icon: <SwipeUp />,
  },
  {
    title: "Scroll down until id visible",
    autocompleteTemplate: 'scrollDownUntilVisible.id: "<element id>"',
    alternatives: ["scroll", "swipe", "slide"],
    icon: <SwipeDown />,
  },
  {
    title: "Scroll up until text visible",
    autocompleteTemplate: 'scrollUpUntilVisible.text: "<text>"',
    alternatives: ["scroll", "swipe", "slide"],
    icon: <SwipeUp />,
  },
  {
    title: "Scroll down until text visible",
    autocompleteTemplate: 'scrollDownUntilVisible.text: "<text>"',
    alternatives: ["scroll", "swipe", "slide"],
    icon: <SwipeDown />,
  },
  {
    title: "Swipe",
    autocompleteTemplate: "swipe: <direction>",
    alternatives: ["swipe", "scroll", "slide"],
    icon: <SwipeLeft />,
  },
  {
    title: "Swipe on element (slide)",
    autocompleteTemplate: "slide: <direction> <percentage> ;<xPos>;<yPos>",
    alternatives: ["swipe", "slide", "scroll"],
    icon: <SwipeLeft />,
  },
  {
    title: "Check",
    autocompleteTemplate:
      "Check if you see <text/element>, if so continue with next step",
    alternatives: ["check", "verify", "assert"],
    icon: <Check />,
  },
  {
    title: "Open deeplink",
    autocompleteTemplate: 'openLink: "<url>"',
    alternatives: ["open", "deeplink"],
    icon: <Link />,
  },
  {
    title: "Open App Clip Link",
    autocompleteTemplate: 'openAppClipLink: "<url>"',
    alternatives: ["appclip", "clip"],
    icon: <Link />,
  },
  {
    title: "Execute curl request",
    autocompleteTemplate: "curlRequest: <curl>",
    alternatives: ["curl", "request", "execute", "network"],
    icon: <SettingsEthernet />,
  },
  {
    title: "Wait",
    autocompleteTemplate: "wait: <number>",
    alternatives: ["wait", "sleep"],
    icon: <HourglassBottom />,
  },
  {
    title: "Assert visible id",
    autocompleteTemplate: 'assertVisible.id: "<element id>"',
    alternatives: ["assert", "visible"],
    icon: <FindInPage />,
  },
  {
    title: "Assert visible text",
    autocompleteTemplate: 'assertVisible.text: "<element id>"',
    alternatives: ["assert", "visible", "text"],
    icon: <FindInPage />,
  },
  {
    title: "Assert not visible id",
    autocompleteTemplate: 'assertNotVisible.id: "<element id>"',
    alternatives: ["assert", "visible", "not"],
    icon: <SearchOff />,
  },
  {
    title: "Assert not visible text",
    autocompleteTemplate: 'assertNotVisible.text: "<element id>"',
    alternatives: ["assert", "visible", "text", "not"],
    icon: <SearchOff />,
  },
  {
    title: "With vision",
    autocompleteTemplate: "withVision: <text>",
    alternatives: ["with", "vision"],
    icon: <RemoveRedEye />,
  },
  {
    title: "Press back button",
    autocompleteTemplate: "pressBackButton",
    alternatives: ["press", "back", "return"],
    icon: <ArrowBack />,
  },
  {
    title: "Press Enter",
    autocompleteTemplate: "press: enter",
    alternatives: ["press", "enter", "return"],
    icon: <KeyboardReturn />,
  },
  {
    title: "Press Home",
    autocompleteTemplate: "go to: home",
    alternatives: ["press", "home"],
    icon: <Home />,
  },
  {
    title: "Launch app",
    autocompleteTemplate: 'launchApp: "<bundle>"',
    alternatives: ["launch", "app", "start"],
    icon: <Launch />,
  },
  {
    title: "Restart app",
    autocompleteTemplate: "restartApp",
    alternatives: ["restart", "app", "reset"],
    icon: <RestartAlt />,
  },
  {
    title: "Remove text",
    autocompleteTemplate: 'removeText: "<text>"',
    alternatives: ["remove", "erase", "delete"],
    icon: <Backspace />,
  },
  {
    title: "Match biometry",
    autocompleteTemplate: "matchBiometry: <true/false>",
    alternatives: ["biometry", "match", "fingerprint"],
    icon: <Fingerprint />,
  },
  {
    title: "Take screenshot",
    autocompleteTemplate: 'takeScreenshot: "<label>"',
    alternatives: ["take", "screenshot", "capture"],
    icon: <Camera />,
  },
  {
    title: "Open info bar",
    autocompleteTemplate: "openInfoBar",
    alternatives: ["info", "bar", "open"],
    icon: <PermDeviceInformation />,
  },
  {
    title: "Close info bar",
    autocompleteTemplate: "closeInfoBar",
    alternatives: ["info", "bar", "close"],
    icon: <PermDeviceInformation />,
  },
  {
    title: "Extract link from email",
    autocompleteTemplate: "extractLinkFromEmail: <email>;<url>",
    alternatives: ["email", "link", "extract"],
    icon: <Email />,
  },
];

const tiptapDynamicCommandsSuggestions = (settingsData: SettingsData) => {
  const suggestions = [...TIPTAP_COMMANDS_SUGGESTIONS];

  if (settingsData.enableOfflineModeCommand) {
    suggestions.push(
      ...[
        {
          title: "Disable network connection",
          autocompleteTemplate: "offlineMode: true",
          alternatives: ["disable", "network", "connection", "offline", "mode"],
          icon: <WifiOff />,
        },
        {
          title: "Enable network connection",
          autocompleteTemplate: "offlineMode: false",
          alternatives: ["enable", "network", "connection", "online", "mode"],
          icon: <Wifi />,
        },
      ]
    );
  }

  return suggestions;
};

const tiptapDoubleCurlySuggestions = (acceptedDynamicValues: string[]) => [
  {
    title: "Date",
    autocompleteTemplate: "{{currentDate}}",
    alternatives: ["date", "day"],
    icon: <CalendarToday />,
  },
  {
    title: "Date formatted",
    autocompleteTemplate: "{{currentDateFormatted}}",
    alternatives: ["date", "day"],
    icon: <CalendarToday />,
  },
  {
    title: "Date plus x-hours ISO 8601 formatted",
    autocompleteTemplate: "{{datetime(<x>)}}",
    alternatives: ["date", "day", "plus", "minus"],
    icon: <CalendarToday />,
  },
  {
    title: "Timestamp",
    autocompleteTemplate: "{{timestamp}}",
    alternatives: ["timestamp"],
    icon: <Abc />,
  },
  {
    title: "Random number with x-digits",
    autocompleteTemplate: "{{randomNumber(<x>)}}",
    alternatives: ["random", "number", "randomNumber"],
    icon: <Casino />,
  },
  {
    title: "Random number in range",
    autocompleteTemplate: "{{randomNumberInRange(<x,y>)}}",
    alternatives: ["random", "number", "randomNumber", "range", "min", "max"],
    icon: <Casino />,
  },
  ...acceptedDynamicValues
    .filter(
      (value) =>
        !["timestamp", "currentDate", "currentDateFormatted"].includes(value)
    )
    .map((envVar) => ({
      title: `Env var <${envVar}>`,
      autocompleteTemplate: `{{${envVar}}}`,
      alternatives: [envVar],
      icon: <DataObject />,
    })),
];

const tiptapTemplatesSuggestions = (templates: TemplateWithLabel[]) => [
  ...templates.map((template) => ({
    title: `${template.label}`,
    autocompleteTemplate: template.label,
    alternatives: [template.label],
    icon: <CropTwoTone />,
    imageUrl: template.imageUrl,
  })),
];

const tiptapAudiosSuggestions = (audios: AudioFile[]) => [
  ...audios.map((audio) => ({
    title: `${audio.label}`,
    autocompleteTemplate: `play: ${audio.name}`,
    alternatives: ["play", audio.name],
    icon: <AudioFileOutlined />,
  })),
];

export {
  tiptapDoubleCurlySuggestions,
  tiptapTemplatesSuggestions,
  tiptapDynamicCommandsSuggestions,
  tiptapAudiosSuggestions,
};
